// src/components/admin/contas/SubOverview.tsx
// GRAIA Admin — Visão Geral: KPIs, distribuição, alertas reais, atividade recente
import React, { useEffect, useState, useCallback, useImperativeHandle } from 'react';
import {
    Users, UserCheck, Activity, AlertTriangle, Info,
    CheckCircle, CreditCard, Package, LayoutGrid,
    X, Trash2, RotateCcw
} from 'lucide-react';

interface Stats {
    total: number;
    vip: number;
    vendor: number;
    editor: number;
    normal: number;
    admin?: number;
}

interface ActivityItem {
    text: string;
    time: string;
    type: 'purchase' | 'grant' | 'expire' | 'system';
}

interface SystemAlert {
    text: string;
    type: 'warning' | 'info' | 'danger';
}

interface OverviewExtra {
    usersWithAccess: number;
    usersWithoutPurchase: number;
    activeSubscriptions: number;
    activeCombos: number;
    activeDecks: number;
    expiredToday: number;
    manualGrants: number;
    interdictedDecks: number;
    expiringSoon?: number;
    recentActivity: ActivityItem[];
    systemAlerts?: SystemAlert[];
}

const typeColor: Record<ActivityItem['type'], string> = {
    purchase: '#003BD1',
    grant: '#8b5cf6',
    expire: '#ef4444',
    system: '#f59e0b',
};

// ══════════════════════════════════════════════════════════════
const SubOverview = React.forwardRef<{ refresh: () => void }, {}>((_, ref) => {
    const token = localStorage.getItem('accessToken');
    const [stats, setStats] = useState<Stats | null>(null);
    const [extra, setExtra] = useState<OverviewExtra | null>(null);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(false);

    // Controle local de itens ocultados (não persiste ao atualizar — intencional)
    const [hiddenIdxs, setHiddenIdxs] = useState<Set<number>>(new Set());
    const [allCleared, setAllCleared] = useState(false);

    const dismissItem = (i: number) =>
        setHiddenIdxs(prev => new Set([...prev, i]));

    const dismissAll = () => setAllCleared(true);

    const restore = () => { setAllCleared(false); setHiddenIdxs(new Set()); };

    // ── Fetch ─────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(false);
        // Ao atualizar, limpa os ocultos para mostrar a lista fresca
        setHiddenIdxs(new Set());
        setAllCleared(false);
        try {
            const [statsRes, extraRes] = await Promise.allSettled([
                fetch('/api/admin/subscriptions/stats', { headers: { Authorization: `Bearer ${token}` } }),
                fetch('/api/admin/subscriptions/overview-extra', { headers: { Authorization: `Bearer ${token}` } }),
            ]);

            if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
                const d = await statsRes.value.json();
                if (d.success) setStats(d.stats);
            }

            if (extraRes.status === 'fulfilled' && extraRes.value.ok) {
                const d = await extraRes.value.json();
                if (d.success) setExtra(d.overview);
            }
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useImperativeHandle(ref, () => ({ refresh: fetchData }), [fetchData]);

    // ── Loading ───────────────────────────────────────────────
    if (loading) return (
        <div className="sub-loading">
            <div className="sub-loading-spinner" />
            Carregando visão geral...
        </div>
    );

    if (!stats) return (
        <div className="sub-empty">
            <p>Não foi possível carregar os dados.{' '}
                <button onClick={fetchData} style={{ background: 'none', border: 'none', color: 'var(--sub-green)', cursor: 'pointer', fontWeight: 700 }}>
                    Tentar novamente
                </button>
            </p>
        </div>
    );

    // ── KPI cards ─────────────────────────────────────────────
    const kpiCards = [
        { label: 'Usuários Totais', value: stats.total, icon: Users, color: 'green' as const },
        { label: 'Com Acesso Ativo', value: extra?.usersWithAccess ?? stats.vip + stats.vendor, icon: CheckCircle, color: 'green' as const },
        { label: 'Sem Compra', value: extra?.usersWithoutPurchase ?? stats.normal, icon: UserCheck, color: '' as const },
        { label: 'Acesso Total Ativo', value: extra?.activeSubscriptions ?? stats.vip, icon: CreditCard, color: 'amber' as const },
        { label: 'Combos Ativos', value: extra?.activeCombos ?? 0, icon: LayoutGrid, color: 'blue' as const },
        { label: 'Decks Individuais', value: extra?.activeDecks ?? 0, icon: Package, color: 'purple' as const },
    ];

    // ── Distribuição ──────────────────────────────────────────
    const total = stats.total || 1;
    const distItems = [
        { label: 'Administradores', value: stats.admin ?? 1, color: '#ef4444' },
        { label: 'VIP', value: stats.vip, color: '#f59e0b' },
        { label: 'Vendedores', value: stats.vendor, color: '#8b5cf6' },
        { label: 'Editores', value: stats.editor, color: '#3b82f6' },
        { label: 'Usuários comuns', value: stats.normal, color: '#003BD1' },
    ];

    // ── Alertas ───────────────────────────────────────────────
    const alerts: { text: string; type: 'warning' | 'info' | 'danger' }[] = [];
    if ((extra?.expiredToday ?? 0) > 0) alerts.push({ text: `${extra!.expiredToday} assinatura(s) expiraram hoje`, type: 'warning' });
    if ((extra?.expiringSoon ?? 0) > 0) alerts.push({ text: `${extra!.expiringSoon} assinatura(s) expiram nos próximos 7 dias`, type: 'warning' });
    if ((extra?.manualGrants ?? 0) > 0) alerts.push({ text: `${extra!.manualGrants} usuário(s) com acesso manual concedido`, type: 'info' });
    if ((extra?.interdictedDecks ?? 0) > 0) alerts.push({ text: `${extra!.interdictedDecks} deck(s) estão interditados`, type: 'danger' });

    if (extra?.systemAlerts) {
        for (const sa of extra.systemAlerts) {
            if (!alerts.some(a => a.text === sa.text)) alerts.push(sa);
        }
    }
    if (alerts.length === 0) alerts.push({ text: 'Nenhum alerta no momento', type: 'info' });

    const alertIcon: Record<string, React.ElementType> = { warning: AlertTriangle, info: Info, danger: AlertTriangle };

    // ── Atividade (com filtro local) ──────────────────────────
    const rawActivity = extra?.recentActivity ?? [];
    const visibleActivity = allCleared
        ? []
        : rawActivity.filter((_, i) => !hiddenIdxs.has(i));
    const hiddenCount = hiddenIdxs.size + (allCleared ? rawActivity.length - hiddenIdxs.size : 0);

    // ═════════════════════════════════════════════════════════
    return (
        <div>
            {/* ── KPIs ─────────────────────────────────────── */}
            <div className="sub-stats-grid sub-stats-grid--6col" style={{ marginBottom: 20 }}>
                {kpiCards.map(card => (
                    <div key={card.label} className={`sub-stat-card ${card.color}`}>
                        <div className={`sub-stat-icon ${card.color}`}><card.icon size={16} /></div>
                        <span className="sub-stat-value">{card.value.toLocaleString('pt-BR')}</span>
                        <span className="sub-stat-label">{card.label}</span>
                    </div>
                ))}
            </div>

            {/* ── Distribuição + Alertas ────────────────────── */}
            <div className="sub-ov-row cols-2">
                <div className="sub-overview-section">
                    <h4 className="sub-overview-section-title"><Users size={15} /> Tipos de conta</h4>
                    <div className="sub-dist-list">
                        {distItems.map(item => (
                            <div key={item.label}>
                                <div className="sub-dist-item">
                                    <span className="sub-dist-dot" style={{ background: item.color }} />
                                    <span className="sub-dist-label">{item.label}</span>
                                    <span className="sub-dist-value">{item.value.toLocaleString('pt-BR')}</span>
                                </div>
                                <div className="sub-dist-bar-track">
                                    <div className="sub-dist-bar-fill" style={{ width: `${Math.round((item.value / total) * 100)}%`, background: item.color, opacity: 0.7 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="sub-overview-section">
                    <h4 className="sub-overview-section-title"><AlertTriangle size={15} /> Alertas do sistema</h4>
                    <div className="sub-alert-list">
                        {alerts.map((a, i) => {
                            const Icon = alertIcon[a.type] ?? Info;
                            return (
                                <div key={i} className={`sub-alert-item ${a.type}`}>
                                    <Icon size={14} /> {a.text}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── Atividade recente ────────────────────────── */}
            <div className="sub-overview-section" style={{ marginBottom: 0 }}>
                {/* Header do feed */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <h4 className="sub-overview-section-title" style={{ margin: 0, flex: 1 }}>
                        <Activity size={15} /> Atividade recente
                        {rawActivity.length > 0 && (
                            <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 600, color: 'var(--sub-text-muted)', textTransform: 'none', letterSpacing: 0 }}>
                                {visibleActivity.length} de {rawActivity.length}
                            </span>
                        )}
                    </h4>

                    {/* Botões de controle — só aparecem quando há itens */}
                    {rawActivity.length > 0 && (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            {hiddenCount > 0 && (
                                <button onClick={restore} title="Restaurar ocultos" style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '4px 9px', borderRadius: 6,
                                    border: '1px solid var(--sub-green-border)',
                                    background: 'var(--sub-green-dim)',
                                    color: 'var(--sub-green)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer'
                                }}>
                                    <RotateCcw size={11} /> Restaurar ({hiddenCount})
                                </button>
                            )}
                            {visibleActivity.length > 0 && (
                                <button onClick={dismissAll} title="Limpar todos" style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '4px 9px', borderRadius: 6,
                                    border: '1px solid rgba(239,68,68,0.2)',
                                    background: 'rgba(239,68,68,0.06)',
                                    color: '#dc2626', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer'
                                }}>
                                    <Trash2 size={11} /> Limpar tudo
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Lista */}
                {rawActivity.length === 0 ? (
                    extra === null ? (
                        <div style={{ fontSize: '0.84rem', color: 'var(--sub-text-muted)', padding: '12px 0' }}>
                            <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 6, color: '#f59e0b' }} />
                            Endpoint <code style={{ fontSize: '0.8rem' }}>/api/admin/subscriptions/overview-extra</code> não encontrado.
                        </div>
                    ) : (
                        <p style={{ fontSize: '0.85rem', color: 'var(--sub-text-muted)', margin: 0 }}>
                            Nenhuma atividade nos últimos 30 dias.
                        </p>
                    )
                ) : visibleActivity.length === 0 ? (
                    /* Tudo foi ocultado */
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0', fontSize: '0.84rem', color: 'var(--sub-text-muted)' }}>
                        <Trash2 size={14} />
                        Todos os itens foram ocultados.{' '}
                        <button onClick={restore} style={{ background: 'none', border: 'none', color: 'var(--sub-green)', cursor: 'pointer', fontWeight: 700, fontSize: '0.84rem', padding: 0 }}>
                            Restaurar
                        </button>
                    </div>
                ) : (
                    <div className="sub-feed-list">
                        {rawActivity.map((item, i) => {
                            if (hiddenIdxs.has(i) || allCleared) return null;
                            return (
                                <div key={i} className="sub-feed-item" style={{ alignItems: 'center' }}>
                                    <span className="sub-feed-dot" style={{ background: typeColor[item.type] ?? '#9ca3af', marginTop: 0, flexShrink: 0 }} />
                                    <span className="sub-feed-text" style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: item.text }} />
                                    <span className="sub-feed-time" style={{ marginRight: 10 }}>{item.time}</span>
                                    {/* Botão dismiss individual */}
                                    <button
                                        onClick={() => dismissItem(i)}
                                        title="Ocultar este item"
                                        style={{
                                            width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                                            border: '1px solid var(--sub-border)',
                                            background: 'transparent',
                                            cursor: 'pointer', display: 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            color: 'var(--sub-text-muted)', transition: 'all 0.13s', padding: 0
                                        }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(239,68,68,0.1)'; (e.currentTarget as HTMLButtonElement).style.color = '#dc2626'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--sub-text-muted)'; }}
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
});

SubOverview.displayName = 'SubOverview';
export default SubOverview;
